// scheduling/utils/optimizeHours.js
// Pure function: distributes totalHours across candidateDates respecting per-day
// capacity. Used by the edit modal's Optimize toggle, the DND preview modal, and
// the PlaceholderAssignModal preview panel.
//
// Distribution is done in integer 0.25h quarters internally — BMSS time entry is
// in 0.25h increments everywhere (Empire timesheets, PE WIP), so optimizer
// output that isn't a clean quarter is friction. Any overflow (totalHours that
// exceeds total free capacity) is frontloaded: the first N days each get one
// extra quarter, so the *last* day always has the smallest portion.
//
// Inputs:
//   staffId           — for getDayCapFn + entries filtering
//   candidateDates    — ISO 'YYYY-MM-DD' array in order
//   totalHours        — total to distribute. Rounded to the nearest 0.25 internally.
//   excludeEntryIds   — Set of EntryIds to exclude from "existing" (edit-mode self)
//   getDayCapFn       — (staffId, iso) => number  (handles holidays/PTO/templates)
//   entriesArr        — raw entries array; used to compute existing hours per day
//   dayHoursMap       — optional precomputed { staffId: { iso: totalHrs } }
//                       (when present, faster than scanning entries; subtract
//                       excludeEntryIds' self-hours from this total)
//
// Output:
//   { dailyHours: { iso: number }, warnings: [{ date, scheduled, capacity }],
//     totalAssigned, unassigned }
//   All hour values in dailyHours / totalAssigned are 0.25h multiples.

const Q = 4; // quarters per hour

function existingScheduledHours({ staffId, iso, dayHoursMap, entriesArr, excludeEntryIds }) {
  if (dayHoursMap) {
    let v = dayHoursMap[staffId]?.[iso] || 0;
    if (excludeEntryIds?.size && entriesArr) {
      const selfHrs = entriesArr.filter(e =>
        e.StaffId === staffId && (typeof e.EntryDate === 'string' ? e.EntryDate : '').slice(0, 10) === iso && excludeEntryIds.has(e.EntryId)
      ).reduce((s, e) => s + (e.Hours || 0), 0);
      v -= selfHrs;
    }
    return v;
  }
  return (entriesArr || []).filter(e =>
    e.StaffId === staffId && (typeof e.EntryDate === 'string' ? e.EntryDate : '').slice(0, 10) === iso && !excludeEntryIds?.has(e.EntryId)
  ).reduce((s, e) => s + (e.Hours || 0), 0);
}

export function computeOptimizedHours({ staffId, candidateDates, totalHours, excludeEntryIds, getDayCapFn, entriesArr, dayHoursMap }) {
  // Round totalHours to the nearest 0.25 so a legacy fractional input doesn't
  // force us to emit a non-quarter day to make the totals match.
  const totalQ = Math.max(0, Math.round((Number(totalHours) || 0) * Q));
  const rawQ = {};                  // { iso: integer quarters }
  let totalAssignedQ = 0;

  // Step 1 — greedy frontload to per-day free capacity.
  // dayFreeQ is FLOORED so a legacy non-quarter `existing` value can't trick
  // the optimizer into over-allocating in step 1. Any tiny leftover becomes
  // overflow handled by step 2.
  for (const iso of candidateDates) {
    const dayCap = getDayCapFn(staffId, iso);
    const existing = existingScheduledHours({ staffId, iso, dayHoursMap, entriesArr, excludeEntryIds });
    const dayFreeQ = Math.max(0, Math.floor((dayCap - existing) * Q));
    const assignQ = Math.min(dayFreeQ, totalQ - totalAssignedQ);
    rawQ[iso] = assignQ;
    totalAssignedQ += assignQ;
    if (totalAssignedQ >= totalQ) break;
  }

  // Step 2 — frontload-remainder overflow. If totalQ couldn't fit inside per-day
  // free capacity, distribute the remaining quarters across eligible days
  // (dayCap > 0). The first `extra` days each get one additional quarter so
  // the last day has the smallest portion.
  let remainingQ = totalQ - totalAssignedQ;
  if (remainingQ > 0) {
    const eligibleDays = candidateDates.filter(iso => getDayCapFn(staffId, iso) > 0);
    if (eligibleDays.length > 0) {
      const baseAdd = Math.floor(remainingQ / eligibleDays.length);
      const extra = remainingQ - baseAdd * eligibleDays.length; // 0..(len-1)
      for (let i = 0; i < eligibleDays.length; i++) {
        const iso = eligibleDays[i];
        const addQ = baseAdd + (i < extra ? 1 : 0);
        rawQ[iso] = (rawQ[iso] || 0) + addQ;
      }
      remainingQ = 0;
    }
  }

  // Convert quarters → hours
  const dailyHours = {};
  for (const [iso, q] of Object.entries(rawQ)) {
    dailyHours[iso] = q / Q;
  }
  const totalAssigned = Object.values(rawQ).reduce((s, q) => s + q, 0) / Q;

  // Generate warnings for over-capacity days (post-distribution).
  const warnings = [];
  for (const iso of candidateDates) {
    if (!dailyHours[iso]) continue;
    const dayCap = getDayCapFn(staffId, iso);
    const existing = dayHoursMap ? (dayHoursMap[staffId]?.[iso] || 0) : 0;
    const selfHrs = excludeEntryIds?.size && entriesArr
      ? entriesArr.filter(e => e.StaffId === staffId && (typeof e.EntryDate === 'string' ? e.EntryDate : '').slice(0, 10) === iso && excludeEntryIds.has(e.EntryId)).reduce((s, e) => s + (e.Hours || 0), 0)
      : 0;
    const dayTotal = existing - selfHrs + dailyHours[iso];
    if (dayTotal > dayCap && dayCap > 0) {
      warnings.push({ date: iso, scheduled: dayTotal, capacity: dayCap });
    }
  }

  // unassigned = quarters we couldn't place (happens only when every candidate
  // day has dayCap <= 0 — step 2 has nowhere to put the remainder).
  return {
    dailyHours,
    warnings,
    totalAssigned,
    unassigned: remainingQ / Q,
  };
}

// Pure function: fill each candidate date to that staff's available capacity
// (cap − existing), skipping PTO days entirely. Total is the *output*, not the
// input — used by the modal's "Fill mode" toggle so users don't have to guess
// the total upfront. Same Q (0.25h) discipline as computeOptimizedHours.
//
// Inputs mirror computeOptimizedHours plus ptoByStaffDay (used as a hard skip).
// Returns the same shape: { dailyHours, warnings, totalAssigned, unassigned }.
// unassigned is always 0 by construction.
export function computeFillToCapacity({ staffId, candidateDates, getDayCapFn, ptoByStaffDay, dayHoursMap, entriesArr, excludeEntryIds }) {
  const dailyHours = {};
  let totalAssignedQ = 0;
  for (const iso of candidateDates) {
    const ptoHrs = ptoByStaffDay?.[staffId]?.[iso] || 0;
    if (ptoHrs > 0) continue;
    const dayCap = getDayCapFn(staffId, iso);
    const existing = existingScheduledHours({ staffId, iso, dayHoursMap, entriesArr, excludeEntryIds });
    const freeQ = Math.max(0, Math.floor((dayCap - existing) * Q));
    if (freeQ <= 0) continue;
    dailyHours[iso] = freeQ / Q;
    totalAssignedQ += freeQ;
  }
  return { dailyHours, warnings: [], totalAssigned: totalAssignedQ / Q, unassigned: 0 };
}

// Pure function: spread totalHours as EVENLY as possible across candidateDates while
// never exceeding any day's free capacity (cap − existing). Where computeOptimizedHours
// front-loads (fill the first days, finish fast), this levels: every day with room gets
// the same share, and a day with less room just takes what it has. It is what a
// multi-week plan wants — Roll Forward's "N hours over W weeks" and the chooser handoff —
// so the work actually spans the weeks the manager picked.
//
// Never overbooks. Hours that don't fit come back as `unassigned` for the caller to show;
// they are not dumped onto the last day the way computeOptimizedHours' overflow is.
// Holidays, PTO, part-time days off and pre-start dates all arrive as dayCap 0, so they
// are skipped without special cases.
//
// The minimum day used by multi-week plans (Roll Forward, the chooser → grid hand-off).
export const MIN_PLANNED_DAY_HOURS = 2;

// minDayHours (optional): never place less than this on a day. A small total then lands
// on a few days spaced evenly through the window — starting with its first day — instead
// of 0.25h slivers on every day (1.75h over 2 weeks was seven 0.25h days). If those days
// lack room it falls back to leveling over every day with room, so the fit is never worse.
//
// Same 0.25h discipline as above. Inputs mirror computeOptimizedHours.
// Returns { dailyHours, totalAssigned, unassigned, freeHours }.
export function spreadWithinCapacity({ staffId, candidateDates, totalHours, getDayCapFn, entriesArr, dayHoursMap, excludeEntryIds, minDayHours = 0 }) {
  const totalQ = Math.max(0, Math.round((Number(totalHours) || 0) * Q));
  const freeQ = (candidateDates || []).map(iso => {
    const dayCap = getDayCapFn(staffId, iso);
    const existing = existingScheduledHours({ staffId, iso, dayHoursMap, entriesArr, excludeEntryIds });
    return Math.max(0, Math.floor((dayCap - existing) * Q));
  });
  const sumFreeQ = freeQ.reduce((s, q) => s + q, 0);

  // Level totalQ over the days in `pick` (indexes into candidateDates): the highest
  // per-day level L with Σ min(free, L) ≤ total, then the remainder one quarter at a
  // time, earliest first, to days that still have room above L.
  const level = (pick) => {
    const out = freeQ.map(() => 0);
    const free = pick.map(i => freeQ[i]);
    if (free.reduce((s, q) => s + q, 0) <= totalQ) {
      pick.forEach((i, j) => { out[i] = free[j]; });
      return out;
    }
    let lo = 0;
    let hi = Math.max(0, ...free);
    while (lo < hi) {
      const mid = Math.ceil((lo + hi) / 2);
      const used = free.reduce((s, q) => s + Math.min(q, mid), 0);
      if (used <= totalQ) lo = mid; else hi = mid - 1;
    }
    pick.forEach((i, j) => { out[i] = Math.min(free[j], lo); });
    let restQ = totalQ - out.reduce((s, q) => s + q, 0);
    for (const i of pick) {
      if (restQ <= 0) break;
      if (freeQ[i] > out[i]) { out[i] += 1; restQ -= 1; }
    }
    return out;
  };

  const withRoom = freeQ.map((q, i) => (q > 0 ? i : -1)).filter(i => i >= 0);
  let assignQ = level(withRoom);
  const minQ = Math.round((Number(minDayHours) || 0) * Q);
  if (minQ > 0 && totalQ > 0) {
    // Days that can take a full minimum chunk (or the whole total, if it's smaller).
    const roomy = freeQ.map((q, i) => (q >= Math.min(minQ, totalQ) ? i : -1)).filter(i => i >= 0);
    const k = Math.min(roomy.length, Math.max(1, Math.floor(totalQ / minQ)));
    if (k > 0 && k < withRoom.length) {
      const pick = Array.from({ length: k }, (_, j) => roomy[Math.floor((j * roomy.length) / k)]);
      const spaced = level(pick);
      if (spaced.reduce((s, q) => s + q, 0) === totalQ) assignQ = spaced;
    }
  }

  const dailyHours = {};
  candidateDates.forEach((iso, i) => { if (assignQ[i] > 0) dailyHours[iso] = assignQ[i] / Q; });
  const assignedQ = assignQ.reduce((s, q) => s + q, 0);
  return {
    dailyHours,
    totalAssigned: assignedQ / Q,
    unassigned: (totalQ - assignedQ) / Q,
    freeHours: sumFreeQ / Q,
  };
}

// Pure helper: evenly divide totalHours across dayCount days in 0.25h multiples.
// Frontloads any remainder so the last day has the smallest portion. Does NOT
// respect capacity — for capacity-aware distribution use computeOptimizedHours.
// Returns: number[] of length dayCount (each value a .25 multiple, in hours).
export function distributeEvenlyQuarters(totalHours, dayCount) {
  if (dayCount <= 0) return [];
  const totalQ = Math.max(0, Math.round((Number(totalHours) || 0) * Q));
  const base = Math.floor(totalQ / dayCount);
  const extra = totalQ - base * dayCount;
  const out = [];
  for (let i = 0; i < dayCount; i++) {
    out.push((base + (i < extra ? 1 : 0)) / Q);
  }
  return out;
}
